/** How far inside the canvas a word is brought when it has to be scrolled to. */
const EDGE_MARGIN_PX = 60

/** The nearest ancestor that scrolls, which is the canvas for a word on it. */
export function findCanvasScroller(element: Element): Element | null {
  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    const { overflowX, overflowY } = getComputedStyle(parent)
    const scrollsX = /(auto|scroll)/.test(overflowX) && parent.scrollWidth > parent.clientWidth
    const scrollsY = /(auto|scroll)/.test(overflowY) && parent.scrollHeight > parent.clientHeight

    if (scrollsX || scrollsY) return parent
  }

  return null
}

/** How far to move so `start..end` is inside `from..to` with `margin` to spare, or 0 if it already is. */
function distanceIntoView(start: number, end: number, from: number, to: number, margin: number) {
  if (start < from) return start - from - margin
  if (end > to) return end - to + margin

  return 0
}

/**
 * Brings the misspelled word the popover is on into the canvas, and only when it is not already
 * there. A word that is on screen leaves the canvas exactly where it is, so stepping between
 * instances does not shift the page under the popover, as in the design demo; the popover finds its
 * own room beside the word (it flips and shifts to stay inside the canvas). A word that is off
 * screen is brought in at once, not scrolled gradually, by the least that puts it inside the canvas
 * with a margin, so the popover can show in its final place.
 */
export function revealSpellWord(element: Element): void {
  const word = element.getBoundingClientRect()
  const scroller = findCanvasScroller(element)
  const view = scroller?.getBoundingClientRect() ?? {
    left: 0,
    top: 0,
    right: window.innerWidth,
    bottom: window.innerHeight,
  }
  const left = distanceIntoView(word.left, word.right, view.left, view.right, EDGE_MARGIN_PX)
  const top = distanceIntoView(word.top, word.bottom, view.top, view.bottom, EDGE_MARGIN_PX)

  if (left === 0 && top === 0) return

  if (scroller && typeof scroller.scrollBy === 'function') {
    scroller.scrollBy({ left, top, behavior: 'instant' })

    return
  }

  element.scrollIntoView?.({ behavior: 'instant', block: 'nearest', inline: 'nearest' })
}
